// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomQuoteLinesModel, CustomQuoteModel } from "@bg/App/Services/CustomQuoteService";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type MyCustomQuoteDetailsState = {
    isLoading: boolean;
    quote: CustomQuoteModel | undefined;
    productsInfo: any;
    realTimePricingFailed: boolean;
    realTimeInventoryFailed: boolean;
    selectedLineIds: { [key: string]: string[] };
    isRequestingQuote: boolean;
    addingToCart: string;
    isCreatingFromOpti: boolean;
    sortBy: "" | "name asc" | "name desc";
    errorLoading: boolean;
    productLinesQtyToUpdate: CustomQuoteLinesModel[];
};

const initialState: MyCustomQuoteDetailsState = {
    isLoading: false,
    quote: undefined,
    selectedLineIds: {},
    realTimePricingFailed: false,
    realTimeInventoryFailed: false,
    productsInfo: {},
    isRequestingQuote: false,
    addingToCart: "",
    isCreatingFromOpti: false,
    errorLoading: false,
    sortBy: "",
    productLinesQtyToUpdate: [],
};

const myCustomQuoteDetails = createTypedReducerWithImmer(initialState, {
    "Pages/MyCustomQuotes/BeginLoadingQuoteDetails": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.isLoading = true;
    },
    "Pages/MyCustomQuotes/CompleteLoadingQuoteDetails": (
        draft: Draft<MyCustomQuoteDetailsState>,
        actions: { quote?: CustomQuoteModel; errorLoading?: boolean },
    ) => {
        draft.isLoading = false;
        if (actions.quote) {
            draft.quote = actions.quote;
        }
        if (actions.errorLoading) {
            draft.errorLoading = actions.errorLoading;
        }
    },
    "Pages/MyCustomQuotes/BeginRequestQuote": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.isRequestingQuote = true;
    },
    "Pages/MyCustomQuotes/CompleteRequestingQuote": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.isRequestingQuote = true;
    },
    "Pages/MyCustomQuotes/BeginAddingToCart": (
        draft: Draft<MyCustomQuoteDetailsState>,
        actions: { quoteId: string },
    ) => {
        draft.addingToCart = actions.quoteId;
    },
    "Pages/MyCustomQuotes/CompleteAddingToCart": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.addingToCart = "";
    },
    "Pages/MyCustomQuotes/BeginCreatingCustomQuote": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.isCreatingFromOpti = true;
    },
    "Pages/MyCustomQuotes/CompleteCreatingCustomQuote": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.isCreatingFromOpti = false;
    },
    "Pages/MyCustomQuotes/ClearLoadedQuote": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.quote = undefined;
        draft.isLoading = false;
        draft.selectedLineIds = {};
        draft.productsInfo = {};
        draft.realTimeInventoryFailed = false;
        draft.realTimeInventoryFailed = false;
    },
    "Pages/MyCustomQuotes/SetProductsInfo": (
        draft: Draft<MyCustomQuoteDetailsState>,
        actions: {
            realTimeInfo: {
                productsInfo: ProductInfo[];
                realTimePricingFailed: boolean;
                realTimeInventoryFailed: boolean;
            };
            quote: CustomQuoteModel;
        },
    ) => {
        const { realTimeInfo, quote } = actions;
        draft.realTimePricingFailed = realTimeInfo.realTimePricingFailed;
        draft.realTimeInventoryFailed = realTimeInfo.realTimeInventoryFailed;

        Object.keys(quote!.groupLinesModel!)?.forEach(group => {
            quote?.groupLinesModel[group]?.forEach(line => {
                const lineInfo = realTimeInfo.productsInfo.filter(info => (info as any).lineId === line.lineId)?.[0];
                if (!draft.productsInfo?.hasOwnProperty(line.lineId)) {
                    draft.productsInfo[line.lineId] = { ...lineInfo };
                }
                if (line.relatedItems) {
                    line.relatedItems.forEach(related => {
                        const lineInfo = realTimeInfo.productsInfo.filter(
                            info => (info as any).lineId === related.lineId,
                        )?.[0];
                        if (!draft.productsInfo?.hasOwnProperty(related.lineId)) {
                            draft.productsInfo[related.lineId] = { ...lineInfo };
                        }
                    });
                }
            });
        });
    },
    "Pages/MyCustomQuotes/UpdateLineQty": (
        draft: Draft<MyCustomQuoteDetailsState>,
        actions: { lineModel: { lineId: string; qty: number } },
    ) => {
        const { lineId, qty } = actions.lineModel;
        if (!lineId || !qty) {
            return;
        }
        draft.productsInfo[lineId].qtyOrdered = qty;
    },
    "Pages/MyCustomQuotes/SetSelectedLines": (
        draft: Draft<MyCustomQuoteDetailsState>,
        actions: {
            groupName?: string;
            lineIds?: string[];
            selectAll?: boolean;
            clearAll?: boolean;
            selectedLines: string[];
        },
    ) => {
        const { groupName, lineIds, selectAll, clearAll, selectedLines } = actions;
        // select all lines for a certain
        if (selectAll && groupName && !clearAll) {
            draft.selectedLineIds[groupName] = [];
            draft.quote?.groupLinesModel[groupName].map(line => draft.selectedLineIds[groupName].push(line.lineId));
            return;
        }

        // clear all lines for a certain group
        if (!selectAll && groupName && clearAll) {
            draft.selectedLineIds[groupName] = [];
        }

        // select all lines for all group
        if (selectAll && !groupName && !lineIds) {
            draft.selectedLineIds = {};
            const groups = draft.quote?.groups.split(",");
            const lines = {} as any;
            groups?.map(group => {
                draft.quote?.groupLinesModel[group] &&
                    draft.quote?.groupLinesModel?.[group].map(line => {
                        if (!lines?.hasOwnProperty(group)) {
                            lines[group] = [];
                        }

                        lines[group].push(line.lineId);
                    });
            });
            draft.selectedLineIds = lines;
            return;
        }

        // select a certain line
        if (!selectAll && lineIds && groupName && !clearAll) {
            if (!selectedLines || !selectedLines.length) {
                draft.selectedLineIds[groupName] = [];
            }
            const idIndex = selectedLines?.indexOf(lineIds[0]);
            if (idIndex > -1) {
                const arr = [...selectedLines];
                arr.splice(idIndex, 1);
                draft.selectedLineIds[groupName] = [...arr];
            } else {
                draft.selectedLineIds[groupName].push(lineIds[0]);
            }
        }

        // after moving lines to another group -> remove them from the selection on old group
        if (!selectAll && lineIds && groupName && clearAll && selectedLines) {
            const arr = [...selectedLines];
            lineIds.forEach(id => {
                const idIndex = arr?.indexOf(id);
                if (idIndex > -1) {
                    arr.splice(idIndex, 1);
                    draft.selectedLineIds[groupName] = [...arr];
                }
            });
        }

        // clear all selected
        if (clearAll && !groupName && !selectAll) {
            draft.selectedLineIds = {};
        }
    },
    "Pages/MyCustomQuotes/UpdateSortBy": (
        draft: Draft<MyCustomQuoteDetailsState>,
        actions: { sortBy: "" | "name asc" | "name desc" },
    ) => {
        draft.sortBy = actions.sortBy;
    },
    "Pages/MyCustomQuotes/UpdateProductsQty": (
        draft: Draft<MyCustomQuoteDetailsState>,
        action: { lineModel: CustomQuoteLinesModel & { originalQty?: number } },
    ) => {
        const existingObjectIndex = draft.productLinesQtyToUpdate.findIndex(
            item => item.lineId === action.lineModel.lineId,
        );

        if (existingObjectIndex !== -1) {
            if (action.lineModel.qty === action.lineModel.originalQty) {
                draft.productLinesQtyToUpdate = draft.productLinesQtyToUpdate.filter(
                    (element, index) => index !== existingObjectIndex,
                );
            } else {
                delete action.lineModel.originalQty;
                draft.productLinesQtyToUpdate[existingObjectIndex] = {
                    ...action.lineModel,
                };
            }
        } else {
            delete action.lineModel.originalQty;
            draft.productLinesQtyToUpdate.push(action.lineModel);
        }
    },
    "Pages/MyCustomQuotes/ClearProductsQty": (draft: Draft<MyCustomQuoteDetailsState>) => {
        draft.productLinesQtyToUpdate = [];
    },
});

(reducers as any).myCustomQuoteDetails = myCustomQuoteDetails;

type myCustomQuoteDetailsReducer = {
    myCustomQuoteDetails: typeof myCustomQuoteDetails;
};

export type myCustomQuoteDetailsActions =
    | Parameters<myCustomQuoteDetailsReducer[keyof myCustomQuoteDetailsReducer]>[1]
    | AnyAction;
